// Every sound the product can play, per event: the listening round's
// candidates (/test/sounds) and, for each event, the one that plays.
//
// Round 2 (operator, 2026-11-05): round 1's synthesized voices sounded "like
// from a game", so the candidates are recordings — real coins, bells, glass —
// from libraries whose licence allows them inside a commercial product. Only
// CC0 (public domain dedication) files from Freesound are used: no
// attribution clause to carry, no "personal use only". Each file names its
// page and licence here and in frontend/public/sounds/LICENSE.md.
import type { SoundEvent } from './settings'
import type { SynthVoice } from './voices'

export interface SoundCredit {
  title: string
  author: string
  /** the sound's own page, where its licence is stated */
  url: string
  license: 'CC0 1.0'
}

interface SoundOptionBase {
  id: string
  /** a few words on how it sounds */
  about: string
}

export interface SynthSoundOption extends SoundOptionBase {
  synth: SynthVoice
}

export interface FileSoundOption extends SoundOptionBase {
  /** served path under frontend/public */
  file: string
  /** seconds into the file where the sound starts (a recording's lead-in) */
  start?: number
  /** seconds played from `start`; a trimmed tail fades over its last 150 ms */
  length?: number
  credit: SoundCredit
}

export type SoundOption = SynthSoundOption | FileSoundOption

export function isFileOption(option: SoundOption): option is FileSoundOption {
  return 'file' in option
}

const ROUND1 = {
  fmBells: {
    id: 'r1-fm-bells',
    about: 'Round 1: two struck bells (synthesized)',
    synth: 'fmBells',
  },
  dryTick: { id: 'r1-dry-tick', about: 'Round 1: crisp dry tick (synthesized)', synth: 'dryTick' },
  woodenTock: {
    id: 'r1-wooden-tock',
    about: 'Round 1: soft wooden tock (synthesized)',
    synth: 'woodenTock',
  },
} satisfies Record<string, SynthSoundOption>

/** A Freesound CC0 recording, served from frontend/public/sounds/fs-<id>.mp3. */
function fs(
  id: number,
  about: string,
  title: string,
  author: string,
  length?: number
): FileSoundOption {
  return {
    id: `fs-${id}`,
    about,
    file: `/sounds/fs-${id}.mp3`,
    ...(length ? { length } : {}),
    credit: { title, author, url: `https://freesound.org/s/${id}/`, license: 'CC0 1.0' },
  }
}

export const SOUND_OPTIONS: Record<SoundEvent, readonly SoundOption[]> = {
  buy: [
    fs(847340, 'One real coin onto a small pile in the palm', 'coin-010', 'ilyaShevelev'),
    fs(847333, 'The same coins, a slightly longer ring', 'coin-003', 'ilyaShevelev'),
    fs(368203, 'One coin onto coins, a clean clink', 'Coin Dropped', 'kermite607'),
    fs(699523, 'A coin dropped into a hand, short', 'Coin_Drop_28', 'Valenspire'),
    fs(184744, 'One coin into a tin, more metallic', 'CoinDrop8.wav', 'NickMorris'),
    fs(728224, 'A register ch-ching, no voice', 'cash register chching', 'disman', 0.8),
  ],
  sell: [
    fs(847349, 'A handful of coins palm to palm', 'handful-of-coins-006', 'ilyaShevelev'),
    fs(201809, 'Clay chips clinking, duller', 'poker_chips5.wav', 'fartheststar'),
    fs(540368, 'Chips shuffled in a hand', 'Handfull of Poker Chips', 'Za-Games'),
    fs(336571, 'Coins poured between hands', 'coins 05.wav', 'Anthousai'),
    fs(187145, 'Coins out of a hand into a box', 'coins from hand into box.wav', 'jankisjes'),
    fs(842172, 'Several coins clinking together', 'Coin Clink Free', 'AardsReal'),
  ],
  tradeFailure: [
    fs(478191, 'A soft beep from a metal bowl', 'Beep_Error_1.wav', 'JonnyRuss01'),
    fs(848748, 'A wooden block on felt, a muted knock', 'tap on felt', 'emapuree'),
    fs(336495, 'A short muffled thud', 'LowHit_01.wav', 'Faulkin'),
    fs(872227, 'A pillow hit, a soft thud', 'Soft Impact - Pillow Hit', 'KoimeMatsuchiya'),
    fs(405705, 'A low mattress hit', 'found matress hit.wav', 'apinasaundi', 0.8),
    fs(722082, 'A struck glass bowl, G4', 'Bowl G4 3', 'snakedersch', 0.8),
  ],
  limitFill: [
    fs(571512, 'One soft bell', 'Soft-Notifications - Bell - HighDing', 'LegitCheese'),
    fs(614832, 'A crystal glass bell', 'crystal-bell-08', 'arseniiv', 1.0),
    fs(614833, 'The crystal bell, another strike', 'crystal-bell-01', 'arseniiv', 1.0),
    fs(262958, 'An empty glass tapped, a ting', 'Glass Tap.wav', 'Unicornaphobist'),
    fs(745014, 'A plucked empty bottle, a ping', 'Glass Ping', 'tix99', 0.9),
    fs(580649, 'A sherry glass struck with a knife', 'ding.wav', 'mark205'),
  ],
  alert: [
    fs(571513, 'A soft bell struck twice', 'Soft-Notifications - Bell - Ding-Dong', 'LegitCheese'),
    fs(192761, 'A shop door bell, double ding', 'Ryuuzan_shop_door_bell00.wav', 'ryuuzan'),
    fs(434627, 'A small reception bell', 'ding.wav', 'Dr. Macak', 1.0),
    fs(531031, 'A small brass bell, da-ding', 'bell8.wav', 'creeeeak', 1.0),
    fs(648960, 'A handheld metal bell', 'Handheld Bell', 'IENBA', 1.0),
    fs(396423, 'A glass bell rung briefly', 'Glass Bell.WAV', 'Joedozer', 1.0),
    // the one round-1 sound the operator kept ("more or less OK")
    ROUND1.fmBells,
  ],
  tracked: [
    fs(813182, 'A tiny ping, water in a glass', 'Tiny Dink', 'ldsqui'),
    fs(740423, 'A short synth notification', 'Message Notification 4', 'AnthonyRox'),
    fs(380482, 'A high short chime', 'Chime Notification', 'Jofae'),
    fs(668384, 'A glass tapped once', 'Glass Tap', 'CJspellsfish'),
    fs(217462, 'A struck metal ping', 'Metal Ping 4.wav', 'JarredGibb'),
    fs(427389, 'A pint glass tap, duller', 'Glass Tap', 'zembacraftworks'),
  ],
  withdrawal: [
    fs(817959, 'Air moving away', 'Whoosh away', 'jriches1'),
    fs(816261, 'A soft low whoosh', 'Broomstick whoosh 2 - Soft low OKM1', 'Sadiquecat'),
    fs(699314, 'Book pages, a swish', 'Book_Misc_09', 'Valenspire'),
    fs(648387, 'Paper slid over paper', 'SlidePaperOnPaper.mp3', 'Korczu'),
    fs(496188, 'A cloth drag, a soft whoosh', 'Whoosh (Clothing Drag)', 'JonasTisell'),
    fs(842511, 'A drumstick through air', 'Woosh_2', 'gulfstreamav'),
  ],
  // not redone in round 2: off by default, the press is a click, not money
  click: [ROUND1.dryTick, ROUND1.woodenTock],
}

/** The option each event plays until the listening round picks: the first
 *  candidate of each, and for the alert the round-1 bell the operator kept. */
export const DEFAULT_SOUND: Record<SoundEvent, string> = {
  buy: 'fs-847340',
  sell: 'fs-847349',
  tradeFailure: 'fs-478191',
  limitFill: 'fs-571512',
  alert: 'r1-fm-bells',
  tracked: 'fs-813182',
  withdrawal: 'fs-817959',
  click: 'r1-dry-tick',
}

/** The event's option by id; its default when the id is not one of them. */
export function soundOption(event: SoundEvent, id: string = DEFAULT_SOUND[event]): SoundOption {
  const options = SOUND_OPTIONS[event]
  return (
    options.find((option) => option.id === id) ??
    options.find((option) => option.id === DEFAULT_SOUND[event]) ??
    (options[0] as SoundOption)
  )
}
